// react
import { useState, useEffect } from 'react';
// mui
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
// date-pickers
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// dayjs
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
// react-admin
import { useListContext } from 'react-admin';

/**
 * DateFilter component for selecting date range and filtering options in Subscription Transactions.
 * Mimics the style and behavior of the Dashboard date filter.
 */
const DateFilter = (props: any) => {
  const { filterValues, setFilters } = useListContext();
  const today = dayjs();
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');

  /**
   * Handles date range selection based on the selected filter option.
   */
  const handleDates = (data: string) => {
    let start: any = null;
    let end: any = null;
    let shouldShow = false;

    if (data === 'month') {
      const startOfPreviousMonth = today.subtract(1, 'month').startOf('month');
      start = startOfPreviousMonth;
      end = startOfPreviousMonth.endOf('month');
    } else if (data === 'week') {
      const prevWeek = today.subtract(7, 'day');
      start = prevWeek.startOf('week');
      end = prevWeek.endOf('week');
    } else if (data === 'last_7_days') {
      start = today.subtract(7, 'day');
      end = today;
    } else if (data === 'custom') {
      shouldShow = true;
      // Don't clear immediately, wait for user to pick
      start = filterValues.startDate ? dayjs(filterValues.startDate) : null;
      end = filterValues.endDate ? dayjs(filterValues.endDate) : null;
    } else if (data === 'reset') {
      start = null;
      end = null;
    }

    setShow(shouldShow);

    if (data !== 'custom') {
      const newFilters = { ...filterValues };
      if (start) {
        newFilters.startDate = start
          .startOf('day')
          .format('YYYY-MM-DDTHH:mm:ss[Z]');
      } else {
        delete newFilters.startDate;
      }

      if (end) {
        newFilters.endDate = end.endOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]');
      } else {
        delete newFilters.endDate;
      }
      setFilters(newFilters, undefined);
    }
  };

  const handleChange = (event: any) => {
    setName(event.target.value);
  };

  const handleCustomDateChange = (type: 'start' | 'end', value: any) => {
    const newFilters = { ...filterValues };
    if (type === 'start') {
      newFilters.startDate = value
        ? dayjs(value).startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]')
        : undefined;
    } else {
      newFilters.endDate = value
        ? dayjs(value).endOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]')
        : undefined;
    }
    setFilters(newFilters, undefined);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ml: 'auto',
        mb: 2,
        justifyContent: 'flex-end',
      }}
    >
      {show && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start date"
            value={
              filterValues.startDate ? dayjs(filterValues.startDate) : null
            }
            onChange={(val) => handleCustomDateChange('start', val)}
            maxDate={filterValues.endDate ? dayjs(filterValues.endDate) : today}
            slotProps={{ textField: { size: 'small', sx: { width: '11rem' } } }}
          />
          <DatePicker
            label="End date"
            value={filterValues.endDate ? dayjs(filterValues.endDate) : null}
            onChange={(val) => handleCustomDateChange('end', val)}
            minDate={
              filterValues.startDate ? dayjs(filterValues.startDate) : undefined
            }
            maxDate={today}
            slotProps={{ textField: { size: 'small', sx: { width: '11rem' } } }}
          />
        </LocalizationProvider>
      )}

      <FormControl sx={{ minWidth: 160 }} size="small">
        <InputLabel id="date-filter-label">Filter By Date</InputLabel>
        <Select
          labelId="date-filter-label"
          id="date-filter-select"
          value={name}
          label="Filter By Date"
          onChange={handleChange}
        >
          <MenuItem
            onClick={() => handleDates('last_7_days')}
            value="Last 7 days"
          >
            Last 7 days
          </MenuItem>
          <MenuItem onClick={() => handleDates('week')} value="Last Week">
            Last Week
          </MenuItem>
          <MenuItem onClick={() => handleDates('month')} value="Last Month">
            Last Month
          </MenuItem>
          <MenuItem onClick={() => handleDates('custom')} value="Custom">
            Custom
          </MenuItem>
          <MenuItem onClick={() => handleDates('reset')} value="Reset">
            Reset
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default DateFilter;
